import { useState, useRef, useCallback, useEffect } from "react";

export interface UseDraggableOptions {
  /** 初始位置（右下角偏移），默认 { right: 16, bottom: 90 } */
  initialRight?: number;
  initialBottom?: number;
  /** 拖拽阈值（px），超过才算拖拽，防止误触点击 */
  threshold?: number;
  /** 元素尺寸（px），用于边界约束 */
  size?: number;
}

export interface UseDraggableReturn {
  /** 绑定到容器的 style（包含 position + left/top/right/bottom） */
  containerStyle: React.CSSProperties;
  /** 绑定到拖拽目标的 onPointerDown */
  onPointerDown: (e: React.PointerEvent) => void;
  /** 当前是否正在拖拽 */
  isDragging: boolean;
  /** 在可点击子元素的 onClick 中先调用，若刚完成一次拖拽则返回 true，应跳过后续逻辑 */
  shouldSkipClick: () => boolean;
  /** 重置到初始位置 */
  resetPosition: () => void;
}

const STORAGE_KEY = "ai-float-btn-pos";

export function useDraggable(options: UseDraggableOptions = {}): UseDraggableReturn {
  const { initialRight = 16, initialBottom = 90, threshold = 5, size = 52 } = options;

  // 从 localStorage 恢复位置（如果有的话）
  const loadSavedPos = useCallback((): { left: number; top: number } | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const pos = JSON.parse(raw) as { left: number; top: number };
        // 校验是否在屏幕范围内
        if (typeof pos.left === "number" && typeof pos.top === "number") return pos;
      }
    } catch { /* ignore */ }
    return null;
  }, []);

  const calcInitialPos = useCallback((): { left: number; top: number } => {
    const saved = loadSavedPos();
    if (saved) return saved;
    const vw = typeof window !== "undefined" ? window.innerWidth : 375;
    const vh = typeof window !== "undefined" ? window.innerHeight : 667;
    return {
      left: vw - initialRight - size,
      top: vh - initialBottom - size,
    };
  }, [initialRight, initialBottom, size, loadSavedPos]);

  const [pos, setPos] = useState(calcInitialPos);
  const [isDragging, setIsDragging] = useState(false);
  const draggingRef = useRef(false);
  const startPointerRef = useRef({ x: 0, y: 0 });
  const startPosRef = useRef({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const skipClickRef = useRef(false);
  const posRef = useRef(pos);
  posRef.current = pos;

  // 窗口 resize 时重新计算初始位置（无保存位置时）
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    setPos(calcInitialPos());
  }, [calcInitialPos]);

  const savePos = useCallback((left: number, top: number) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ left, top }));
    } catch { /* ignore */ }
  }, []);

  const clamp = useCallback(
    (x: number, y: number) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      return {
        left: Math.max(0, Math.min(x, vw - size)),
        top: Math.max(0, Math.min(y, vh - size)),
      };
    },
    [size],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      draggingRef.current = false;
      movedRef.current = false;
      startPointerRef.current = { x: e.clientX, y: e.clientY };
      startPosRef.current = { x: posRef.current.left, y: posRef.current.top };

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startPointerRef.current.x;
        const dy = ev.clientY - startPointerRef.current.y;

        if (!movedRef.current && Math.hypot(dx, dy) > threshold) {
          movedRef.current = true;
          draggingRef.current = true;
          setIsDragging(true);
        }

        if (movedRef.current) {
          const clamped = clamp(startPosRef.current.x + dx, startPosRef.current.y + dy);
          setPos(clamped);
        }
      };

      const onUp = (ev: PointerEvent) => {
        (ev.target as HTMLElement).releasePointerCapture(ev.pointerId);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);

        // 拖拽结束后自动吸附到最近的左/右边
        if (draggingRef.current) {
          const vw = window.innerWidth;
          const mid = vw / 2;
          const currentPos = posRef.current;
          const snapX = currentPos.left + size / 2 > mid ? vw - size - initialRight : initialRight;
          const snapped = { left: snapX, top: currentPos.top };
          setPos(snapped);
          savePos(snapX, snapped.top);
        }

        skipClickRef.current = movedRef.current;

        // 延迟重置 dragging 标记，让 click 事件可以检查
        requestAnimationFrame(() => {
          draggingRef.current = false;
          setIsDragging(false);
        });
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [threshold, clamp, size, initialRight, savePos],
  );

  const shouldSkipClick = useCallback(() => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return true;
    }
    return false;
  }, []);

  const resetPosition = useCallback(() => {
    const p = calcInitialPos();
    setPos(p);
    localStorage.removeItem(STORAGE_KEY);
  }, [calcInitialPos]);

  const containerStyle: React.CSSProperties = {
    position: "fixed",
    left: pos.left,
    top: pos.top,
    right: "auto",
    bottom: "auto",
    zIndex: 60,
    /** 避免块级 fixed 在部分浏览器中横向占满视口，盖住底部 z-50 的弹层点击 */
    width: "fit-content",
    height: "fit-content",
    maxWidth: "100vw",
    /** 根容器不接收点击，避免透明区域挡住页面（如 file input）；子节点用 pointer-events-auto */
    pointerEvents: "none",
    touchAction: "none",
    userSelect: "none",
    transition: isDragging ? "none" : "left 0.25s cubic-bezier(0.34,1.56,0.64,1), top 0.25s cubic-bezier(0.34,1.56,0.64,1)",
  };

  return { containerStyle, onPointerDown, isDragging, shouldSkipClick, resetPosition };
}
